<template>
  <div ref="el" :class="classes" @blur="open = false">
    <div
      class="select__field"
      tabindex="0"
      role="combobox"
      :aria-expanded="open"
      @click="open = !open"
    >
      {{ displayLabel }}
    </div>
    <div class="select__options" role="listbox" aria-label="Available options">
      <div v-if="props.nullable" role="option" @click="onClear">
        {{ props.placeholder || "&nbsp;" }}
      </div>
      <div
        v-for="option in props.options"
        :key="option.value"
        :class="{ active: isSelected(option) }"
        :title="option.label"
        role="option"
        @click="() => onSelect(option)"
      >
        {{ option.label }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { onClickOutside } from "@vueuse/core";
  import type { SelectOption } from "~/lib/types";

  const props = defineProps<{
    options: SelectOption[];
    default?: SelectOption | SelectOption[] | null;
    placeholder?: string;
    nullable?: boolean;
    compact?: boolean;
    outline?: boolean;
    multiSelect?: boolean;
  }>();

  const { locale } = useI18n();

  const emit = defineEmits<{
    (e: "select", value: SelectOption | SelectOption[] | null): void;
  }>();

  const el = ref<HTMLElement | null>(null);
  const open = ref<boolean>(false);

  const selected = ref<SelectOption | SelectOption[] | null>(
    props.multiSelect
      ? Array.isArray(props.default)
        ? props.default
        : []
      : (props.default ?? null)
  );

  const displayLabel = computed(() => {
    if (props.multiSelect && Array.isArray(selected.value)) {
      const count = selected.value.length;

      if (count === 0) return props.placeholder || "";
      if (count === 1) return selected.value[0].label;

      const isCzech = locale.value?.toLowerCase().startsWith("cs");

      if (isCzech) {
        if (count >= 2 && count <= 4) return `Vybrány ${count} položky`;
        return `Vybráno ${count} položek`;
      }

      return `${count} items selected`;
    }

    return (selected.value as SelectOption)?.label || props.placeholder || "";
  });

  const isSelected = (option: SelectOption) => {
    if (props.multiSelect && Array.isArray(selected.value)) {
      return selected.value.some((item) => item.value === option.value);
    }
    return (selected.value as SelectOption)?.value === option.value;
  };

  const onSelect = (option: SelectOption) => {
    if (props.multiSelect) {
      if (!Array.isArray(selected.value)) {
        selected.value = [];
      }

      const index = selected.value.findIndex(
        (item) => item.value === option.value
      );

      if (index > -1) {
        selected.value.splice(index, 1);
      } else {
        selected.value.push(option);
      }

      emit("select", selected.value);
    } else {
      selected.value = option;
      emit("select", option);
      open.value = false;
      clearQueryParams();
    }
  };

  const onClear = () => {
    if (props.multiSelect) {
      selected.value = [];
      emit("select", []);
    } else {
      selected.value = null;
      emit("select", null);
      open.value = false;
    }
  };

  const clearQueryParams = () => {
    const path = window.location.pathname;
    window.history.replaceState({}, "", path);
  };

  onClickOutside(el, () => (open.value = false));

  const isEmpty = computed(() => {
    if (props.multiSelect && Array.isArray(selected.value)) {
      return selected.value.length === 0;
    }
    return !selected.value;
  });

  const classes = computed(() => [
    "select",
    isEmpty.value ? "select--empty" : "",
    open.value ? "select--open" : "",
    props.compact ? "select--compact" : "",
    props.outline ? "select--outline" : "",
  ]);

  watch(
    () => props.default,
    (newValue) => {
      selected.value = props.multiSelect
        ? Array.isArray(newValue)
          ? newValue
          : []
        : (newValue ?? null);
    }
  );
</script>

<style lang="scss">
  .select {
    color: var(--color-dark);
    text-align: left;
    user-select: none;
    position: relative;

    &__field {
      flex: 1;
      height: _rem(56px);
      padding: 0 _rem(16px);
      border: 1px solid var(--color-dark-16-lines);
      border-radius: _rem(8px);
      background-color: var(--color-white);
      display: flex;
      align-items: center;
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &:focus {
        border-color: var(--color-gold);
        outline: none;
      }

      &:after {
        content: "";
        display: block;
        width: _rem(9px);
        height: _rem(9px);
        border-right: 2px solid;
        border-bottom: 2px solid;
        transform: rotate(45deg);
        position: absolute;
        right: _rem(18px);
        top: calc(50% - _rem(6px));
      }
    }

    &:after {
      content: "";
      display: block;
      width: _rem(9px);
      height: _rem(9px);
      border-right: 2px solid;
      border-bottom: 2px solid;
      transform: rotate(45deg);
      position: absolute;
      right: _rem(18px);
      top: calc(50% - _rem(6px));
    }

    &--open &__field {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      &:after {
        transform: rotate(-135deg);
        top: calc(50% - _rem(2px));
      }
    }

    &--empty &__field {
      color: var(--color-light-50);
    }

    &--compact &__field {
      height: _rem(40px);
    }

    &--outline &__field {
      background-color: transparent;
    }

    &__options {
      border-radius: 0px 0px 6px 6px;
      overflow: hidden;
      position: absolute;
      background-color: var(--color-white);
      border: 1px solid var(--color-dark-16-lines);
      border-top: none;
      left: 0;
      right: 0;
      z-index: 1;
      display: none;
      max-height: _rem(240px);
      overflow-y: auto;

      > div {
        padding: _rem(16px) _rem(16px);
        user-select: none;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        cursor: pointer;

        &.active {
          font-weight: 500;
          color: #333;
          background-color: var(--color-dark-8-bg);
        }

        &:hover {
          background-color: var(--color-dark-8-bg);
        }
      }
    }

    &--open &__options {
      display: block;
    }
  }
</style>
